import React from 'react';
import { StickyNote } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@roger/ui-components';
import type { EpisodeNotePreview } from '../../services/api/types.gen';
import { formatEpisodeNoteTimestamp } from '../../utils/episodeNotes';

/** Purple is the episode-note colour everywhere (EpisodeNotesPopover, Hotbox). */
const PILL_CLASS =
  'inline-flex max-w-[9rem] shrink-0 cursor-pointer items-center gap-1 rounded border border-purple-200 bg-purple-50 px-1.5 py-0.5 text-xs font-medium text-purple-700 hover:bg-purple-100';
const ICON_CLASS =
  'inline-flex h-8 w-8 items-center justify-center rounded border border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100 hover:text-purple-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

export type EpisodeNotesPillProps = {
  /** Newest first; the caller sorts. Renders nothing when empty. */
  notes: EpisodeNotePreview[];
  ariaLabel: string;
  /** `pill` shows the newest body inline; `icon` shows it on hover instead. */
  variant?: 'pill' | 'icon';
  side?: 'top' | 'bottom';
  'data-testid'?: string;
};

/**
 * The read-only purple episode-note sticky: a click lists every note of the
 * episode in a popover. Shared by the Hotbox Notes column and the Patient
 * Manager table so both read the same notes the same way.
 */
export const EpisodeNotesPill: React.FC<EpisodeNotesPillProps> = ({
  notes,
  ariaLabel,
  variant = 'pill',
  side = 'top',
  'data-testid': testId = 'episode-notes-preview',
}) => {
  const newest = notes[0];
  if (!newest) return null;

  const trigger = (
    <PopoverTrigger asChild>
      <button
        type='button'
        className={variant === 'icon' ? ICON_CLASS : PILL_CLASS}
        data-testid={testId}
        aria-label={ariaLabel}
        onClick={event => event.stopPropagation()}
      >
        <StickyNote
          className={variant === 'icon' ? 'h-4 w-4' : 'h-3 w-3 shrink-0'}
          aria-hidden='true'
        />
        {variant === 'pill' && <span className='truncate'>{newest.body}</span>}
      </button>
    </PopoverTrigger>
  );

  return (
    <Popover>
      {variant === 'icon' ? (
        <Tooltip>
          <TooltipTrigger asChild>{trigger}</TooltipTrigger>
          <TooltipContent side='top' className='max-w-sm'>
            <p className='whitespace-pre-line'>{newest.body}</p>
            <p className='mt-1 text-xs opacity-80'>{newest.author_name}</p>
          </TooltipContent>
        </Tooltip>
      ) : (
        trigger
      )}
      <PopoverContent
        side={side}
        align='end'
        collisionPadding={12}
        className='max-h-80 w-auto max-w-sm overflow-y-auto p-0'
      >
        <div className='px-3 py-2' data-testid='episode-notes-tooltip'>
          <p className='text-sm font-semibold'>Episode notes</p>
          <div className='mt-1 divide-y divide-border'>
            {notes.map(note => {
              const timestamp = formatEpisodeNoteTimestamp(note.created_at);
              return (
                <div
                  key={note.id}
                  className='space-y-1 py-2 first:pt-1 last:pb-0'
                >
                  <p className='text-xs font-semibold'>
                    {note.author_name}
                    {timestamp ? ` · ${timestamp}` : ''}
                  </p>
                  <p className='whitespace-pre-line text-sm leading-snug'>
                    {note.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default EpisodeNotesPill;
